import { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import DonutChart from "../../components/DonutChart";
import { Shades } from "../../utils/common";
import { useSearchParams } from "react-router-dom";
import { Download, Plus, X, Loader2, Eye, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import PromptModal from "./PromptModal";
import AiAnalysisModal from "./AiAnalysisModal";

// ---------- Subcomponents ----------
const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="5" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="19" r="1" fill="currentColor" />
  </svg>
);

const NoDataFound = () => (
  <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>
    <span className="text-gray-400 text-sm font-medium">No Data Found</span>
  </div>
);

const MetricCard = ({ title, value = "0 s" }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col relative">
    <div className="flex justify-between items-start mb-1">
      <span className="text-xs text-gray-500 font-medium leading-tight pr-4">{title}</span>
      <button className="text-gray-400 hover:text-gray-600 mt-0.5 flex-shrink-0">
        <MoreIcon />
      </button>
    </div>
    <div className="flex items-center justify-center flex-1 py-4">
      <span className="text-5xl font-light text-gray-800 tracking-tight">{value}</span>
    </div>
  </div>
);

// Pagination bar shown below each calls table
const PaginationBar = ({ page, limit, total, onPageChange, disabled }) => {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (limit || 10)));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startItem = total === 0 ? 0 : (safePage - 1) * limit + 1;
  const endItem = Math.min(safePage * limit, total || 0);

  const goTo = (p) => {
    if (p < 1 || p > totalPages || p === safePage || disabled) return;
    onPageChange(p);
  };

  // Build a compact page list with ellipses
  const getPageList = () => {
    const pages = [];
    const windowSize = 1;
    const add = (p) => pages.push(p);
    add(1);
    for (let p = safePage - windowSize; p <= safePage + windowSize; p++) {
      if (p > 1 && p < totalPages) add(p);
    }
    if (totalPages > 1) add(totalPages);
    // de-dupe + sort
    const unique = Array.from(new Set(pages)).sort((a, b) => a - b);
    // insert ellipsis markers
    const withEllipsis = [];
    unique.forEach((p, idx) => {
      if (idx > 0 && p - unique[idx - 1] > 1) withEllipsis.push("ellipsis-" + p);
      withEllipsis.push(p);
    });
    return withEllipsis;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-2 py-3 border-t border-gray-100 text-xs">
      <span className="text-gray-500">
        {total ? (
          <>
            Showing <span className="font-medium text-gray-700">{startItem}</span>-<span className="font-medium text-gray-700">{endItem}</span> of <span className="font-medium text-gray-700">{total}</span> calls
          </>
        ) : (
          "No calls"
        )}
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => goTo(safePage - 1)} disabled={safePage <= 1 || disabled} className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition">
          <ChevronLeft size={14} />
        </button>
        {getPageList().map((p) =>
          typeof p === "string" ? (
            <span key={p} className="px-1 text-gray-400">
              …
            </span>
          ) : (
            <button key={p} onClick={() => goTo(p)} disabled={disabled} className={`min-w-[28px] h-7 px-1.5 rounded-md text-xs font-medium transition ${p === safePage ? "bg-sky-400 text-white" : "text-gray-600 hover:bg-gray-100 border border-gray-200"}`}>
              {p}
            </button>
          ),
        )}
        <button onClick={() => goTo(safePage + 1)} disabled={safePage >= totalPages || disabled} className="flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition">
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

// ---------- Main Component ----------
export default function CallsDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showQuickFilterModal, setShowQuickFilterModal] = useState(false);
  const [kixieAttendees, setKixieAttendees] = useState([]);
  const [ghlAttendees, setGhlAttendees] = useState([]);

  // AI Analysis state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [selectedCallSource, setSelectedCallSource] = useState(null);
  const [generating, setGenerating] = useState({});

  // Prompt modal state
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [pendingCallId, setPendingCallId] = useState(null);
  const [pendingSource, setPendingSource] = useState(null);
  const [pendingOptions, setPendingOptions] = useState({}); // NEW: store regenerate flags
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);

  const [aiPrompt, setAiPrompt] = useState("");

  // ---------- Pagination state ----------
  const PAGE_LIMIT = 50;
  const defaultPagination = { page: 1, limit: PAGE_LIMIT, total: 0 };
  const [kixiePagination, setKixiePagination] = useState({
    outgoing: { ...defaultPagination },
    incoming: { ...defaultPagination },
  });
  const [ghlPagination, setGhlPagination] = useState({
    outbound: { ...defaultPagination },
    inbound: { ...defaultPagination },
  });
  // Per-table loading flags so paginating one table doesn't spin the whole page
  const [tableLoading, setTableLoading] = useState({
    "kixie-outgoing": false,
    "kixie-incoming": false,
    "ghl-outbound": false,
    "ghl-inbound": false,
  });

  // ---------- Date & Filter helpers ----------
  const getTodayUTCFilter = () => {
    const now = new Date();
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return {
      from: from.toISOString().split("T")[0],
      to: to.toISOString().split("T")[0],
    };
  };

  const [quickFilter, setQuickFilter] = useState(() => {
    const defaultFilter = getTodayUTCFilter();
    return {
      from: searchParams.get("from") || defaultFilter.from,
      to: searchParams.get("to") || defaultFilter.to,
      agent: searchParams.get("agent") || "",
      kixieAgent: searchParams.get("kixieAgent") || "",
    };
  });

  const defaultCallState = {
    calls: [],
    total_calls: 0,
    total_duration: 0,
    total_amount: 0,
    calls_with_duration: 0,
    calls_without_duration: 0,
    avg_duration: 0,
    calls_by_attendee: [],
  };

  const [kixieCalls, setKixieCalls] = useState({
    outgoing: defaultCallState,
    incoming: defaultCallState,
  });

  const [ghlCalls, setGhlCalls] = useState({
    outbound: defaultCallState,
    inbound: defaultCallState,
  });

  const [exporting, setExporting] = useState(false);

  const buildDateFilters = (filters, mode = "ghl") => {
    let fromDate = null;
    let toDate = null;
    if (filters?.from) {
      fromDate = new Date(`${filters.from}T00:00:00.000Z`);
    }
    if (filters?.to) {
      toDate = new Date(`${filters.to}T23:59:59.999Z`);
    }
    return {
      from_date: fromDate?.toISOString(),
      to_date: toDate?.toISOString(),
      agent: mode === "ghl" ? filters?.agent || undefined : filters.kixieAgent || undefined,
    };
  };

  const mapCallResponse = (data) => ({
    calls: data?.calls || [],
    total_calls: data?.total_calls || 0,
    total_duration: data?.total_duration || 0,
    total_amount: data?.total_amount || 0,
    calls_with_duration: data?.calls_with_duration || 0,
    calls_without_duration: data?.calls_without_duration || 0,
    avg_duration: data?.avg_duration || 0,
    calls_by_attendee: data?.calls_by_attendee || [],
    calls_by_status: data?.calls_by_status || [],
  });

  // ---------- API fetch functions ----------

  const fetchAiAnalysisPrompt = async () => {
    try {
      const { data } = await axiosInstance.get("api/webhooks/ai-prompt?prompt_type=-7", {});
      if (data?.data?.length) {
        setAiPrompt(data.data[0].prompt_text);
      }
    } catch (error) {
      console.error("error getting AI prompt.", error);
    }
  };

  const fetchKixieAttendees = async () => {
    try {
      const { data } = await axiosInstance.get("api/webhooks/kixie/attendees", {});
      if (data?.success) {
        setKixieAttendees(data.attendees || []);
      }
    } catch (error) {
      console.error("error getting kixie attendees.", error);
    }
  };

  const fetchGhlAttendees = async () => {
    try {
      const { data } = await axiosInstance.get("api/webhooks/ghl/attendees", {});
      if (data?.success) {
        setGhlAttendees(data.attendees || []);
      }
    } catch (error) {
      console.error("error getting ghl attendees.", error);
    }
  };

  const fetchKixieCalls = async ({ type = "outgoing", filters = quickFilter, page = 1, loader = false }) => {
    try {
      if (loader) setLoading(true);
      setTableLoading((prev) => ({ ...prev, [`kixie-${type}`]: true }));
      const { data } = await axiosInstance.get("api/webhooks/kixie/calls", {
        params: {
          page,
          limit: PAGE_LIMIT,
          calltype: type,
          ...buildDateFilters(filters, "kixie"),
        },
      });
      if (data?.success) {
        setKixieCalls((prev) => ({
          ...prev,
          [type]: mapCallResponse(data.data),
        }));
        setKixiePagination((prev) => ({
          ...prev,
          [type]: {
            page: data.data?.page || page,
            limit: data.data?.limit || PAGE_LIMIT,
            total: data.data?.total_calls || 0,
          },
        }));
      }
    } catch (error) {
      console.error("error getting kixie calls.", error);
    } finally {
      setLoading(false);
      setTableLoading((prev) => ({ ...prev, [`kixie-${type}`]: false }));
    }
  };

  const fetchGHLCalls = async ({ type = "outbound", filters = quickFilter, page = 1, loader = false }) => {
    try {
      if (loader) setLoading(true);
      setTableLoading((prev) => ({ ...prev, [`ghl-${type}`]: true }));
      const { data } = await axiosInstance.get("api/webhooks/ghl/calls", {
        params: {
          page,
          limit: PAGE_LIMIT,
          direction: type,
          ...buildDateFilters(filters),
        },
      });
      if (data?.success) {
        setGhlCalls((prev) => ({
          ...prev,
          [type]: mapCallResponse(data.data),
        }));
        setGhlPagination((prev) => ({
          ...prev,
          [type]: {
            page: data.data?.page || page,
            limit: data.data?.limit || PAGE_LIMIT,
            total: data.data?.total_calls || 0,
          },
        }));
      }
    } catch (error) {
      console.error("error getting ghl calls.", error);
    } finally {
      setLoading(false);
      setTableLoading((prev) => ({ ...prev, [`ghl-${type}`]: false }));
    }
  };

  useEffect(() => {
    fetchAiAnalysisPrompt();
    fetchKixieAttendees();
    fetchGhlAttendees();
  }, []);

  useEffect(() => {
    const filters = {
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
      agent: searchParams.get("agent") || "",
      kixieAgent: searchParams.get("kixieAgent") || "",
    };
    setQuickFilter(filters);
    // Reset all tables back to page 1 whenever filters change
    setKixiePagination({ outgoing: { ...defaultPagination }, incoming: { ...defaultPagination } });
    setGhlPagination({ outbound: { ...defaultPagination }, inbound: { ...defaultPagination } });
    fetchKixieCalls({ type: "outgoing", filters, page: 1, loader: true });
    fetchKixieCalls({ type: "incoming", filters, page: 1, loader: true });
    fetchGHLCalls({ type: "outbound", filters, page: 1, loader: true });
    fetchGHLCalls({ type: "inbound", filters, page: 1, loader: true }); // eslint-disable-next-line
  }, [searchParams]);

  const handleApplyFilter = async () => {
    const params = {
      from: quickFilter.from,
      to: quickFilter.to,
      agent: quickFilter.agent,
      kixieAgent: quickFilter.kixieAgent,
    };
    setShowQuickFilterModal(false);
    setSearchParams(params);
    // Note: the searchParams effect above will re-fetch page 1 for all tables
  };

  // ---------- Pagination handlers ----------
  const handleKixiePageChange = (type, newPage) => {
    fetchKixieCalls({ type, filters: quickFilter, page: newPage });
  };

  const handleGhlPageChange = (type, newPage) => {
    fetchGHLCalls({ type, filters: quickFilter, page: newPage });
  };

  const formatDuration = (seconds) => {
    const totalSeconds = parseInt(seconds || 0, 10);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hrs > 0) {
      return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    if (mins > 0) {
      return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }
    return `${secs}s`;
  };

  // ---------- AI Analysis Functions ----------
  const enhanceCall = async (callId, source, prompt, options = {}) => {
    try {
      setGenerating((prev) => ({ ...prev, [callId]: true }));
      const payload = {
        call_id: callId,
        source: source,
        prompt: prompt,
        ...options,
      };
      const { data } = await axiosInstance.post("api/webhooks/call-enhance", payload);

      // Refresh the table data (optional, but keeps lists in sync), staying on the current page
      await refreshAllData();

      if (data?.success) {
        toast.success("Analysis completed successfully.");
        return data; // contains transcript, enhanced, etc.
      } else {
        toast.error(data?.message || "Failed to generate analysis.");
        return null;
      }
    } catch (error) {
      console.error("Enhance error:", error);
      toast.error(error?.response?.data?.message || "Error generating analysis.");
      return null;
    } finally {
      setGenerating((prev) => ({ ...prev, [callId]: false }));
    }
  };

  const refreshAllData = async () => {
    const filters = quickFilter;
    await Promise.all([fetchKixieCalls({ type: "outgoing", filters, page: kixiePagination.outgoing.page }), fetchKixieCalls({ type: "incoming", filters, page: kixiePagination.incoming.page }), fetchGHLCalls({ type: "outbound", filters, page: ghlPagination.outbound.page }), fetchGHLCalls({ type: "inbound", filters, page: ghlPagination.inbound.page })]);
  };

  const openAIModal = (call, source) => {
    setSelectedCall(call);
    setSelectedCallSource(source);
    setAiModalOpen(true);
  };

  const closeAIModal = () => {
    setAiModalOpen(false);
    setSelectedCall(null);
    setSelectedCallSource(null);
  };

  // Open prompt modal with optional regeneration flags
  const openPromptModal = (callId, source, options = {}) => {
    setPendingCallId(callId);
    setPendingSource(source);
    setPendingOptions(options);
    setShowPromptModal(true);
  };

  const handleGenerateWithPrompt = async (customPrompt) => {
    setIsGeneratingPrompt(true);
    setShowPromptModal(false);

    const result = await enhanceCall(pendingCallId, pendingSource, customPrompt, pendingOptions);

    if (result) {
      setSelectedCall((prev) => ({
        ...prev,
        ai_transcript: result.transcript,
        ai_analized_transcript: result.analizedTranscript,
        ai_enhanced_response: result.enhanced,
        ai_status: "completed",
        ai_error_message: null,
      }));
    }

    setIsGeneratingPrompt(false);
    setPendingCallId(null);
    setPendingSource(null);
    setPendingOptions({});
  };

  const findUpdatedCall = (callId, source) => {
    if (source === "ghl") {
      const all = [...ghlCalls.inbound.calls, ...ghlCalls.outbound.calls];
      return all.find((c) => c.id === callId) || null;
    } else if (source === "kixie") {
      const all = [...kixieCalls.incoming.calls, ...kixieCalls.outgoing.calls];
      return all.find((c) => c.id === callId) || null;
    }
    return null;
  };

  // Handlers for regeneration buttons in the AI modal
  const handleRegenerateSTTandAI = (call, source) => {
    // closeAIModal();
    // openAIModal(call, source);
    openPromptModal(call.id, source, { regenerate_stt: true, regenerate_ai_enhance: true });
  };

  const handleRegenerateAIOnly = (call, source) => {
    // closeAIModal();
    // openAIModal(call, source);
    openPromptModal(call.id, source, { regenerate_ai_enhance: true, regenerate_stt: false });
  };

  // ---------- Export ----------
  const handleExport = async () => {
    try {
      setExporting(true);

      const [kixieOutgoingRes, kixieIncomingRes, ghlOutboundRes, ghlInboundRes] = await Promise.all([
        axiosInstance.get("api/webhooks/kixie/calls", {
          params: {
            fetch_all: true,
            calltype: "outgoing",
            ...buildDateFilters(quickFilter),
          },
        }),
        axiosInstance.get("api/webhooks/kixie/calls", {
          params: {
            fetch_all: true,
            calltype: "incoming",
            ...buildDateFilters(quickFilter),
          },
        }),
        axiosInstance.get("api/webhooks/ghl/calls", {
          params: {
            fetch_all: true,
            direction: "outbound",
            ...buildDateFilters(quickFilter),
          },
        }),
        axiosInstance.get("api/webhooks/ghl/calls", {
          params: {
            fetch_all: true,
            direction: "inbound",
            ...buildDateFilters(quickFilter),
          },
        }),
      ]);

      const kixieOutgoing = kixieOutgoingRes.data.data;
      const kixieIncoming = kixieIncomingRes.data.data;
      const ghlOutbound = ghlOutboundRes.data.data;
      const ghlInbound = ghlInboundRes.data.data;

      const createSheet = (data) => {
        const sheet = XLSX.utils.json_to_sheet(data || []);
        autoFitColumns(sheet, data);
        return sheet;
      };

      const formatAttendees = (attendees = []) => {
        return attendees.map((item) => ({
          "Agent Name": item.caller_number || item.call_from || item.calleridName,
          "Total Calls": item.total_calls,
          "Total Duration": item.total_duration,
        }));
      };

      const formatKixieCalls = (calls = []) => {
        return calls.map((call) => ({
          "Call ID": call.callid,
          "Call Type": call.calltype || call.kixieCallDetails?.calltype,
          "Call Status": call.kixieCallDetails?.callstatus,
          Disposition: call.disposition,
          "From Number": call.kixieCallDetails?.fromnumber,
          "To Number": call.kixieCallDetails?.tonumber,
          "Agent Name": call.kixieCallDetails?.calleridName,
          "Call Date": call.kixieCallDetails?.calldate,
          "Duration (sec)": call.kixieCallDetails?.duration,
          "First Name": call.kixiePowerlistContactDetails?.firstName,
          "Last Name": call.kixiePowerlistContactDetails?.lastName,
          Company: call.kixiePowerlistContactDetails?.companyName,
          "Last Dial Outcome": call.kixiePowerlistContactDetails?.lastDialOutcome,
          "Recording URL": call.kixieCallDetails?.recordingurl,
        }));
      };

      const formatGHLCalls = (calls = []) => {
        return calls.map((call) => ({
          "Full Name": call.fullName,
          Phone: call.phone,
          Direction: call.direction,
          From: call.call_from,
          To: call.call_to,
          "Agent Name": call.user_name,
          "Duration (sec)": call.call_duration,
          "Start Time": call.call_start_time,
          "End Time": call.call_end_time,
          Status: call.call_status,
          Disposition: call.call_disposition,
          "Recording URL": call.recording_url,
        }));
      };

      const autoFitColumns = (sheet, data = []) => {
        if (!data.length) return;
        const colWidths = Object.keys(data[0]).map((key) => {
          const maxLength = Math.max(key.length, ...data.map((row) => (row[key] !== null && row[key] !== undefined ? String(row[key]).length : 0)));
          return { wch: Math.min(maxLength + 4, 200) };
        });
        sheet["!cols"] = colWidths;
      };

      // KIXIE OUTBOUND
      const kixieOutCallsSheet = createSheet(formatKixieCalls(kixieOutgoing.calls));
      const kixieOutAttendeeSheet = createSheet(formatAttendees(kixieOutgoing.calls_by_attendee));
      const kixieOutSummarySheet = createSheet([
        {
          "Total Calls": kixieOutgoing.total_calls,
          "Total Duration In Seconds": kixieOutgoing.total_duration,
          "Total Amount": kixieOutgoing.total_amount,
          "Average Duration In Seconds": kixieOutgoing.avg_duration,
        },
      ]);

      const kixieInCallsSheet = createSheet(formatKixieCalls(kixieIncoming.calls));
      const kixieInAttendeeSheet = createSheet(formatAttendees(kixieIncoming.calls_by_attendee));
      const kixieInSummarySheet = createSheet([
        {
          "Total Calls": kixieIncoming.total_calls,
          "Total Duration In Seconds": kixieIncoming.total_duration,
          "Total Amount": kixieIncoming.total_amount,
          "Average Duration In Seconds": kixieIncoming.avg_duration,
        },
      ]);

      const ghlOutCallsSheet = createSheet(formatGHLCalls(ghlOutbound.calls));
      const ghlOutAttendeeSheet = createSheet(formatAttendees(ghlOutbound.calls_by_attendee));
      const ghlOutSummarySheet = createSheet([
        {
          "Total Calls": ghlOutbound.total_calls,
          "Total Duration In Seconds": ghlOutbound.total_duration,
          "Average Duration In Seconds": ghlOutbound.avg_duration,
        },
      ]);

      const ghlInCallsSheet = createSheet(formatGHLCalls(ghlInbound.calls));
      const ghlInAttendeeSheet = createSheet(formatAttendees(ghlInbound.calls_by_attendee));
      const ghlInSummarySheet = createSheet([
        {
          "Total Calls": ghlInbound.total_calls,
          "Total Duration In Seconds": ghlInbound.total_duration,
          "Average Duration In Seconds": ghlInbound.avg_duration,
        },
      ]);

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, kixieOutCallsSheet, "Kixie Out Calls");
      XLSX.utils.book_append_sheet(wb, kixieOutAttendeeSheet, "Kixie Out Attendees");
      XLSX.utils.book_append_sheet(wb, kixieOutSummarySheet, "Kixie Out Summary");

      XLSX.utils.book_append_sheet(wb, kixieInCallsSheet, "Kixie In Calls");
      XLSX.utils.book_append_sheet(wb, kixieInAttendeeSheet, "Kixie In Attendees");
      XLSX.utils.book_append_sheet(wb, kixieInSummarySheet, "Kixie In Summary");

      XLSX.utils.book_append_sheet(wb, ghlOutCallsSheet, "GHL Out Calls");
      XLSX.utils.book_append_sheet(wb, ghlOutAttendeeSheet, "GHL Out Attendees");
      XLSX.utils.book_append_sheet(wb, ghlOutSummarySheet, "GHL Out Summary");

      XLSX.utils.book_append_sheet(wb, ghlInCallsSheet, "GHL In Calls");
      XLSX.utils.book_append_sheet(wb, ghlInAttendeeSheet, "GHL In Attendees");
      XLSX.utils.book_append_sheet(wb, ghlInSummarySheet, "GHL In Summary");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], { type: "application/octet-stream" });
      const formatFileDate = (date) => (date ? date.replaceAll("-", "_") : null);
      const fromDate = formatFileDate(quickFilter?.from) || "today_start";
      const toDate = formatFileDate(quickFilter?.to) || "today_end";
      saveAs(file, `calls_export_${fromDate}_to_${toDate}.xlsx`);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  // ---------- Render Helper for AI Column ----------
  const renderAiColumn = (call, source) => {
    const hasAi = call.ai_analysis_id != null;
    const isGenerating = generating[call.id] || false;

    const getRecordingUrl = (call) => {
      return call.recording_url || call.kixieCallDetails?.recordingurl || null;
    };

    const recordingUrl = getRecordingUrl(call);
    const hasRecording = recordingUrl && recordingUrl.trim() !== "";

    if (hasAi) {
      return (
        <button onClick={() => openAIModal(call, source)} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded transition">
          <Eye size={14} />
          View
        </button>
      );
    }

    return (
      <button onClick={() => hasRecording && openPromptModal(call.id, source)} disabled={!hasRecording || isGenerating} className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition ${!hasRecording ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-sky-400 text-white hover:bg-sky-500 disabled:bg-sky-200 disabled:cursor-wait"}`} title={!hasRecording ? "Recording not available" : ""}>
        {isGenerating ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Sparkles size={14} />
            Generate
          </>
        )}
      </button>
    );
  };

  // ---------- Main Render ----------
  return (
    <div className="min-h-screen bg-gray-100 p-1 sm:p-6 font-sans">
      {/* Header with filters and export */}
      <div className="max-w-[1600px] mx-auto flex items-center justify-between w-full">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <button onClick={() => setShowQuickFilterModal(true)} className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all">
              <Plus size={15} />
              <span>Quick Filters</span>
            </button>
            {(quickFilter.from || quickFilter.to || quickFilter.agent) && (
              <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                {quickFilter.from || "----"} - {quickFilter.to || "----"}
                {quickFilter.agent ? ` | Agent: ${quickFilter.agent}` : ""}
                {quickFilter.kixieAgent ? ` | Kixie Agent: ${quickFilter.kixieAgent}` : ""}
              </div>
            )}
          </div>

          {showQuickFilterModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-gray-800">Quick Filters</h2>
                  <button onClick={() => setShowQuickFilterModal(false)} className="text-gray-400 hover:text-gray-600 text-lg">
                    <X size={15} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From Date</label>
                    <input type="date" value={quickFilter.from} onChange={(e) => setQuickFilter((prev) => ({ ...prev, from: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To Date</label>
                    <input type="date" value={quickFilter.to} onChange={(e) => setQuickFilter((prev) => ({ ...prev, to: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">GHL Agent</label>
                    <select value={quickFilter.agent} onChange={(e) => setQuickFilter((prev) => ({ ...prev, agent: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="">Select GHL Agent</option>
                      {(ghlAttendees || []).map((agent, index) => (
                        <option key={`ghl-${index}`} value={agent}>
                          {agent || "Unknown"}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Kixie Agent</label>
                    <select value={quickFilter.kixieAgent || ""} onChange={(e) => setQuickFilter((prev) => ({ ...prev, kixieAgent: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="">Select Kixie Agent</option>
                      {(kixieAttendees || []).map((agent, index) => (
                        <option key={`kixie-${index}`} value={agent}>
                          {agent || "Unknown"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                  <button
                    onClick={() => {
                      setQuickFilter({ from: "", to: "", agent: "", kixieAgent: "" });
                      setShowQuickFilterModal(false);
                    }}
                    className="px-4 py-2 min-w-32 text-sm rounded-md shadow-sm border hover:bg-gray-200 duration-200 ease-in-out"
                  >
                    Clear
                  </button>
                  <button onClick={handleApplyFilter} className="px-4 py-2 min-w-32 text-sm rounded-md shadow-sm bg-sky-400 hover:bg-sky-600 duration-200 ease-in-out text-white">
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div>
          <button onClick={handleExport} disabled={exporting} className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 disabled:bg-sky-200 disabled:text-black disabled:cursor-wait ease-in-out duration-200 transition-all">
            <Download size={15} />
            <span>{exporting ? "Exporting..." : "Export Data"}</span>
          </button>
        </div>
      </div>

      {/* ========== GHL Incoming ========== */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl shadow-sm my-4 mx-auto max-w-[1600px] p-1 sm:p-4">
        <p className="text-xl font-semibold text-gray-700 text-left mb-4">GHL Incoming Calls</p>
        <div className="grid gap-4 grid-cols-1 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="grid grid-cols-1 gap-4">
              <MetricCard title="Incoming Calls Duration" value={formatDuration(ghlCalls.inbound.total_duration)} />
              <MetricCard title="Incoming Average Call Duration" value={formatDuration(ghlCalls.inbound.avg_duration)} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls Received by Call Attendee</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={ghlCalls.inbound?.calls_by_attendee?.map((d) => d.total_calls).slice(0, 5)} labels={ghlCalls.inbound?.calls_by_attendee?.map((d) => d.caller_number).slice(0, 5)} rounded={true} centerValue={ghlCalls.inbound?.calls_by_attendee?.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls Received by Call Status</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={ghlCalls.inbound?.calls_by_status?.map((d) => d.total_calls).slice(0, 5)} labels={ghlCalls.inbound?.calls_by_status?.map((d) => d.status).slice(0, 5)} rounded={true} centerValue={ghlCalls.inbound?.calls_by_status?.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs text-gray-500 font-medium">Incoming Calls GHL</span>
            <button className="text-gray-400 hover:text-gray-600">
              <MoreIcon />
            </button>
          </div>
          <div className="w-full h-full overflow-hidden">
            {!ghlCalls.inbound.calls.length ? (
              <NoDataFound />
            ) : (
              <div className="w-full max-h-64 overflow-auto relative">
                <table className="w-full text-xs m-0 border-collapse">
                  <thead className="sticky top-0 z-10 bg-sky-400 text-white shadow-sm shadow-sky-100">
                    <tr>
                      <th className="border-none p-2">Name</th>
                      <th className="border-none p-2">Phone</th>
                      <th className="border-none p-2">Agent</th>
                      <th className="border-none p-2">First Time</th>
                      <th className="border-none p-2">Type</th>
                      <th className="border-none p-2">Status</th>
                      <th className="border-none p-2">Disposition</th>
                      <th className="border-none p-2">Duration</th>
                      <th className="border-none p-2">Date</th>
                      <th className="border-none p-2">Recording</th>
                      <th className="border-none p-2 text-nowrap">Enhance Transcript</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ghlCalls.inbound.calls.map((call, index) => (
                      <tr key={call.id} className={`border-t text-[10px] ${index % 2 === 0 ? "bg-sky-300/0" : "bg-sky-300/10"} hover:bg-sky-300/50 transition-colors`}>
                        <td className="border-none p-2 text-nowrap">{call.fullName || call.contactName}</td>
                        <td className="border-none p-2">{call.call_to}</td>
                        <td className="border-none p-2 text-nowrap">{call.user_name || "Unknown"}</td>
                        <td className="border-none p-2 text-nowrap capitalize">{call.first_time_call || "No"}</td>
                        <td className="border-none p-2 capitalize">{call.direction === "inbound" ? "Incoming" : "Outgoing"}</td>
                        <td className="border-none p-2">{call.call_status}</td>
                        <td className="border-none p-2">{call.call_disposition}</td>
                        <td className="border-none p-2">{formatDuration(call.call_duration)}</td>
                        <td className="border-none p-2 text-nowrap">{new Date(call.call_start_time).toLocaleString()}</td>
                        <td className="border-none p-2 min-w-[220px]">
                          <div className="audio-wrapper w-full">{call.call_duration > 0 && call.recording_url ? <audio controls src={call.recording_url} className="w-full" /> : "N/A"}</div>
                        </td>
                        <td className="border-none p-2 text-center">
                          <div className="flex justify-center items-center">{renderAiColumn(call, "ghl")}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <PaginationBar page={ghlPagination.inbound.page} limit={ghlPagination.inbound.limit} total={ghlPagination.inbound.total} onPageChange={(p) => handleGhlPageChange("inbound", p)} disabled={tableLoading["ghl-inbound"]} />
        </div>
      </div>

      {/* ========== GHL Outgoing ========== */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl shadow-sm my-4 mx-auto max-w-[1600px] p-1 sm:p-4">
        <p className="text-xl font-semibold text-gray-700 text-left mb-4">GHL Outgoing Calls</p>
        <div className="grid gap-4 grid-cols-1 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="grid grid-cols-1 gap-4">
              <MetricCard title="Outgoing Calls Duration" value={formatDuration(ghlCalls.outbound.total_duration)} />
              <MetricCard title="Outgoing Average Call Duration" value={formatDuration(ghlCalls.outbound.avg_duration)} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls by Call Attendee</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={ghlCalls.outbound?.calls_by_attendee?.map((d) => d.total_calls).slice(0, 5)} labels={ghlCalls.outbound?.calls_by_attendee?.map((d) => d.caller_number).slice(0, 5)} rounded={true} centerValue={ghlCalls.outbound?.calls_by_attendee?.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls by Call Status</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={ghlCalls.outbound?.calls_by_status?.map((d) => d.total_calls).slice(0, 5)} labels={ghlCalls.outbound?.calls_by_status?.map((d) => d.status).slice(0, 5)} rounded={true} centerValue={ghlCalls.outbound?.calls_by_status?.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs text-gray-500 font-medium">Outgoing Calls</span>
            <button className="text-gray-400 hover:text-gray-600">
              <MoreIcon />
            </button>
          </div>
          <div className="w-full h-full overflow-hidden">
            {!ghlCalls.outbound.calls.length ? (
              <NoDataFound />
            ) : (
              <div className="w-full max-h-64 overflow-auto relative">
                <table className="w-full text-xs m-0 border-collapse">
                  <thead className="sticky top-0 z-10 bg-sky-400 text-white shadow-sm shadow-sky-100">
                    <tr>
                      <th className="border-none p-2">Name</th>
                      <th className="border-none p-2">Phone</th>
                      <th className="border-none p-2">Agent</th>
                      <th className="border-none p-2">First Time</th>
                      <th className="border-none p-2">Type</th>
                      <th className="border-none p-2">Status</th>
                      <th className="border-none p-2">Disposition</th>
                      <th className="border-none p-2">Duration</th>
                      <th className="border-none p-2">Date</th>
                      <th className="border-none p-2">Recording</th>
                      <th className="border-none p-2 text-nowrap">Enhance Transcript</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ghlCalls.outbound.calls.map((call, index) => (
                      <tr key={call.id} className={`border-t text-[10px] ${index % 2 === 0 ? "bg-sky-300/0" : "bg-sky-300/10"} hover:bg-sky-300/50 transition-colors`}>
                        <td className="border-none p-2 text-nowrap">{call.fullName || call.contactName}</td>
                        <td className="border-none p-2">{call.call_to}</td>
                        <td className="border-none p-2">{call.user_name || "Unknown"}</td>
                        <td className="border-none p-2 text-nowrap capitalize">{call.first_time_call || "No"}</td>
                        <td className="border-none p-2 capitalize">{call.direction === "inbound" ? "Incoming" : "Outgoing"}</td>
                        <td className="border-none p-2">{call.call_status}</td>
                        <td className="border-none p-2">{call.call_disposition}</td>
                        <td className="border-none p-2">{formatDuration(call.call_duration)}</td>
                        <td className="border-none p-2 text-nowrap">{new Date(call.call_start_time).toLocaleString()}</td>
                        <td className="border-none p-2 min-w-[220px]">
                          <div className="audio-wrapper w-full">{call.call_duration > 0 && call.recording_url ? <audio controls src={call.recording_url} className="w-full" /> : "N/A"}</div>
                        </td>
                        <td className="border-none p-2 text-center">
                          <div className="flex justify-center items-center">{renderAiColumn(call, "ghl")}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <PaginationBar page={ghlPagination.outbound.page} limit={ghlPagination.outbound.limit} total={ghlPagination.outbound.total} onPageChange={(p) => handleGhlPageChange("outbound", p)} disabled={tableLoading["ghl-outbound"]} />
        </div>
      </div>

      {/* ========== Kixie Incoming ========== */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl shadow-sm my-4 mx-auto max-w-[1600px] p-1 sm:p-4">
        <p className="text-xl font-semibold text-gray-700 text-left mb-4">Kixie Incoming Calls</p>
        <div className="grid gap-4 grid-cols-1 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="grid grid-cols-1 gap-4">
              <MetricCard title="Incoming Calls Duration" value={formatDuration(kixieCalls.incoming.total_duration)} />
              <MetricCard title="Incoming Average Call Duration" value={formatDuration(kixieCalls.incoming.avg_duration)} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls by Call Attendee</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={kixieCalls.incoming.calls_by_attendee.map((d) => d.total_calls).slice(0, 5)} labels={kixieCalls.incoming.calls_by_attendee.map((d) => d.calleridName).slice(0, 5)} rounded={true} centerValue={kixieCalls.incoming.calls_by_attendee.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls by Call Status</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={kixieCalls.incoming.calls_by_status?.map((d) => d.total_calls).slice(0, 5)} labels={kixieCalls.incoming.calls_by_status?.map((d) => d.status).slice(0, 5)} rounded={true} centerValue={kixieCalls.incoming.calls_by_status?.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs text-gray-500 font-medium">Incoming Calls</span>
            <button className="text-gray-400 hover:text-gray-600">
              <MoreIcon />
            </button>
          </div>
          <div className="w-full h-full overflow-hidden">
            {!kixieCalls.incoming.calls.length ? (
              <NoDataFound />
            ) : (
              <div className="w-full max-h-64 overflow-auto relative">
                <table className="w-full text-xs m-0 border-collapse">
                  <thead className="sticky top-0 z-10 bg-sky-400 text-white shadow-sm shadow-sky-100">
                    <tr>
                      <th className="border-none p-2">Name</th>
                      <th className="border-none p-2">Phone</th>
                      <th className="border-none p-2">Agent</th>
                      <th className="border-none p-2">First Time</th>
                      <th className="border-none p-2">Type</th>
                      <th className="border-none p-2">Status</th>
                      <th className="border-none p-2">Disposition</th>
                      <th className="border-none p-2">Duration</th>
                      <th className="border-none p-2">Date</th>
                      <th className="border-none p-2">Recording</th>
                      <th className="border-none p-2 text-nowrap">Enhance Transcript</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kixieCalls.incoming.calls.map((call, index) => (
                      <tr key={call.id} className={`border-t text-[10px] ${index % 2 === 0 ? "bg-sky-300/0" : "bg-sky-300/10"} hover:bg-sky-300/50 transition-colors`}>
                        <td className="border-none p-2">
                          {call.kixiePowerlistContactDetails?.firstName} {call.kixiePowerlistContactDetails?.lastName}
                        </td>
                        <td className="border-none p-2">{call.phone || call.kixieCallDetails?.tonumber}</td>
                        <td className="border-none p-2 capitalize">{call.kixieCallDetails?.calleridName || "Unknown"}</td>
                        <td className="border-none p-2 text-nowrap capitalize">{call.first_time_call || "No"}</td>
                        <td className="border-none p-2 capitalize">{call.calltype || call.kixieCallDetails?.calltype}</td>
                        <td className="border-none p-2">{call.kixieCallDetails?.callstatus}</td>
                        <td className="border-none p-2">{call.disposition}</td>
                        <td className="border-none p-2">{formatDuration(call.kixieCallDetails?.duration)}</td>
                        <td className="border-none p-2 text-nowrap">{new Date(call.kixieCallDetails?.calldate).toLocaleString()}</td>
                        <td className="border-none p-2 min-w-[220px]">
                          <div className="audio-wrapper w-full">{call.kixieCallDetails?.duration > 0 && call.kixieCallDetails?.recordingurl ? <audio controls src={call.kixieCallDetails.recordingurl} className="w-full" /> : "N/A"}</div>
                        </td>
                        <td className="border-none p-2 text-center">
                          <div className="flex justify-center items-center">{renderAiColumn(call, "kixie")}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <PaginationBar page={kixiePagination.incoming.page} limit={kixiePagination.incoming.limit} total={kixiePagination.incoming.total} onPageChange={(p) => handleKixiePageChange("incoming", p)} disabled={tableLoading["kixie-incoming"]} />
        </div>
      </div>

      {/* ========== Kixie Outgoing ========== */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl shadow-sm my-4 mx-auto max-w-[1600px] p-1 sm:p-4">
        <p className="text-xl font-semibold text-gray-700 text-left mb-4">Kixie Outgoing Calls</p>
        <div className="grid gap-4 grid-cols-1 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="grid grid-cols-1 gap-4">
              <MetricCard title="Outgoing Calls Duration" value={formatDuration(kixieCalls.outgoing.total_duration)} />
              <MetricCard title="Outgoing Average Call Duration" value={formatDuration(kixieCalls.outgoing.avg_duration)} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls by Call Attendee</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={kixieCalls.outgoing.calls_by_attendee.map((d) => d.total_calls).slice(0, 5)} labels={kixieCalls.outgoing.calls_by_attendee.map((d) => d.calleridName).slice(0, 5)} rounded={true} centerValue={kixieCalls.outgoing.calls_by_attendee.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col md:col-span-2">
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs text-gray-500 font-medium">Total Calls by Call Status</span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreIcon />
                </button>
              </div>
              <DonutChart data={kixieCalls.outgoing.calls_by_status?.map((d) => d.total_calls).slice(0, 5)} labels={kixieCalls.outgoing.calls_by_status?.map((d) => d.status).slice(0, 5)} rounded={true} centerValue={kixieCalls.outgoing.calls_by_status?.reduce((sum, d) => sum + d.total_calls, 0)} centerLabel={false} donutWidth={17} colors={Shades.sky} height="180px" width="180px" loading={loading} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-col">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs text-gray-500 font-medium">Outgoing Calls</span>
            <button className="text-gray-400 hover:text-gray-600">
              <MoreIcon />
            </button>
          </div>
          <div className="w-full h-full overflow-hidden">
            {!kixieCalls.outgoing.calls.length ? (
              <NoDataFound />
            ) : (
              <div className="w-full max-h-64 overflow-auto relative">
                <table className="w-full text-xs m-0 border-collapse">
                  <thead className="sticky top-0 z-10 bg-sky-400 text-white shadow-sm shadow-sky-100">
                    <tr>
                      <th className="border-none p-2">Name</th>
                      <th className="border-none p-2">Phone</th>
                      <th className="border-none p-2">Agent</th>
                      <th className="border-none p-2">First Time</th>
                      <th className="border-none p-2">Type</th>
                      <th className="border-none p-2">Status</th>
                      <th className="border-none p-2">Disposition</th>
                      <th className="border-none p-2">Duration</th>
                      <th className="border-none p-2">Date</th>
                      <th className="border-none p-2">Recording</th>
                      <th className="border-none p-2 text-nowrap">Enhance Transcript</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kixieCalls.outgoing.calls.map((call, index) => (
                      <tr key={call.id} className={`border-t text-[10px] ${index % 2 === 0 ? "bg-sky-300/0" : "bg-sky-300/10"} hover:bg-sky-300/50 transition-colors`}>
                        <td className="border-none p-2">
                          {call.kixiePowerlistContactDetails?.firstName} {call.kixiePowerlistContactDetails?.lastName}
                        </td>
                        <td className="border-none p-2">{call.phone || call.kixieCallDetails?.tonumber}</td>
                        <td className="border-none p-2 capitalize">{call.kixieCallDetails?.calleridName || "Unknown"}</td>
                        <td className="border-none p-2 text-nowrap capitalize">{call.first_time_call || "No"}</td>
                        <td className="border-none p-2 capitalize">{call.calltype || call.kixieCallDetails?.calltype}</td>
                        <td className="border-none p-2">{call.kixieCallDetails?.callstatus}</td>
                        <td className="border-none p-2">{call.disposition}</td>
                        <td className="border-none p-2">{formatDuration(call.kixieCallDetails?.duration)}</td>
                        <td className="border-none p-2 text-nowrap">{new Date(call.kixieCallDetails?.calldate).toLocaleString()}</td>
                        <td className="border-none p-2 min-w-[220px]">
                          <div className="audio-wrapper w-full">{call.kixieCallDetails?.duration > 0 && call.kixieCallDetails?.recordingurl ? <audio controls src={call.kixieCallDetails.recordingurl} className="w-full" /> : "N/A"}</div>
                        </td>
                        <td className="border-none p-2 text-center">
                          <div className="flex justify-center items-center">{renderAiColumn(call, "kixie")}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <PaginationBar page={kixiePagination.outgoing.page} limit={kixiePagination.outgoing.limit} total={kixiePagination.outgoing.total} onPageChange={(p) => handleKixiePageChange("outgoing", p)} disabled={tableLoading["kixie-outgoing"]} />
        </div>
      </div>

      {/* ========== AI Analysis Modal ========== */}
      <AiAnalysisModal isOpen={aiModalOpen} onClose={closeAIModal} call={selectedCall} source={selectedCallSource} onRegenerateSTTandAI={handleRegenerateSTTandAI} onRegenerateAIOnly={handleRegenerateAIOnly} generating={generating} />

      {/* ========== Prompt Editor Modal ========== */}
      <PromptModal isOpen={showPromptModal} onClose={() => setShowPromptModal(false)} onGenerate={handleGenerateWithPrompt} initialPrompt={aiPrompt} generating={isGeneratingPrompt} />
    </div>
  );
}
